import { useForm, useWatch } from "react-hook-form";
import {
  Box,
  ButtonBase,
  Tooltip,
  useTheme,
  ClickAwayListener,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useImperativeHandle, useState } from "react";
import z from "zod";
import "./filters_bar.scss";
import { useDebounce, useMountedEffect } from "utils/customHooks";
import { BrandButton } from "../button/button";
import { Add, ArrowDropDown, Close } from "@mui/icons-material";
import {
  COLORS_BTN,
  VARIATIONS_BTN,
} from "components/Common/ButtonV2/constants";
import { BrandSearchTextInput, Spacer } from "components";
import { colors } from "assets/colors/colors";
import cn from "classnames";

const FILTERS_BAR_FORM_ID = "filters-bar-form";

const FILTERS_BAR_SEARCH_FIELD = "search";
const searchFieldSchema = z.string().optional();
export const FiltersBar = forwardRef(
  (
    {
      onFiltersChanged = () => {},
      schemaResolver,
      filtersBuilder,
      createButton,
      className,
      filtersFormClassName,
      isSearchable = true,
      defaultValues,
    },
    ref,
  ) => {
    const [submittedFiltersCount, setSubmittedFiltersCount] = useState(() => {
      if (!defaultValues || typeof defaultValues !== "object") return 0;

      return Object.entries(defaultValues).filter(([key, value]) => {
        if (key === FILTERS_BAR_SEARCH_FIELD) return false;
        if (value === null || value === undefined) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }).length;
    });
    const [isOpen, setIsOpen] = useState(false);

    const filtersForm = useForm({
      defaultValues,
      resolver: zodResolver(
        (schemaResolver || z.object({})).extend({
          [FILTERS_BAR_SEARCH_FIELD]: searchFieldSchema,
        }),
      ),
    });

    useImperativeHandle(ref, () => filtersForm, []);

    const { control, handleSubmit, reset } = filtersForm;

    const formValues = useWatch({ control });

    const debouncedSearch = useDebounce(formValues.search || "", 500);

    useMountedEffect(() => {
      onFiltersChanged(formValues);
    }, [debouncedSearch]);

    const countSelectedFilters = () => {
      if (!formValues || typeof formValues !== "object") return 0;

      return Object.entries(formValues).filter(([key, value]) => {
        if (key === FILTERS_BAR_SEARCH_FIELD) return false;
        if (value === null || value === undefined) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }).length;
    };

    const onSubmitFilters = (values) => {
      handleClose();
      onFiltersChanged(values);
      setSubmittedFiltersCount(countSelectedFilters());
    };

    const onFiltersChipPressed = () => {
      setIsOpen(!isOpen);
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const onClearAllPressed = (event) => {
      event.stopPropagation();
      reset({
        [FILTERS_BAR_SEARCH_FIELD]:
          formValues?.[FILTERS_BAR_SEARCH_FIELD] || "",
      });
      setSubmittedFiltersCount(0);
      onFiltersChanged({
        [FILTERS_BAR_SEARCH_FIELD]:
          formValues?.[FILTERS_BAR_SEARCH_FIELD] || "",
      });
    };

    const theme = useTheme();

    return (
      <div className={cn("filters-bar", className)}>
        {isSearchable && (
          <BrandSearchTextInput
            className="filters-bar__input"
            formControl={control}
            field="search"
            placeholder="Search"
          />
        )}
        {schemaResolver ? (
          <ClickAwayListener onClickAway={handleClose}>
            <Tooltip
              open={isOpen}
              placement="bottom-start"
              disableFocusListener
              disableHoverListener
              disableTouchListener
              slotProps={{
                popper: {
                  sx: {
                    maxWidth: "400px",
                    width: "100%",
                    boxSizing: "border-box",
                    zIndex: 1299,
                  },
                },
                tooltip: {
                  sx: {
                    color: "unset",
                    maxWidth: "initial !important",
                    backgroundColor: theme.palette.common.white,
                    boxShadow: theme.shadows[1],
                  },
                },
              }}
              title={
                <Box
                  id={FILTERS_BAR_FORM_ID}
                  component="form"
                  className={cn(
                    "filters-bar__filters-form",
                    filtersFormClassName,
                  )}
                  onSubmit={handleSubmit(onSubmitFilters)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="filters-bar__filters-form__filters">
                    {filtersBuilder?.({ formControl: control })}{" "}
                  </div>
                  <div className="filters-bar__filters-form__actions">
                    <BrandButton variant="outlined" onClick={handleClose}>
                      Cancel
                    </BrandButton>
                    <BrandButton formId={FILTERS_BAR_FORM_ID} type="submit">
                      Apply
                    </BrandButton>
                  </div>
                </Box>
              }
            >
              <div className="filters-bar__filters">
                <FiltersChip
                  onPressed={onFiltersChipPressed}
                  submittedFiltersCount={submittedFiltersCount}
                />
                {submittedFiltersCount > 0 && (
                  <BrandButton
                    variant="text"
                    onClick={onClearAllPressed}
                    endIcon={<Close />}
                    size="small"
                  >
                    Clear All
                  </BrandButton>
                )}
              </div>
            </Tooltip>
          </ClickAwayListener>
        ) : (
          <Spacer />
        )}
        {createButton}
      </div>
    );
  },
);

export const FiltersBarCreateButton = ({ children, ...props }) => {
  return (
    <div className="filters-bar__add-button">
      <BrandButton
        uppercase
        variant={VARIATIONS_BTN.TEXT}
        color={COLORS_BTN.PRIMARY}
        startIcon={<Add />}
        fullWidth
        {...props}
      >
        {children}
      </BrandButton>
    </div>
  );
};

const FiltersChip = ({ onPressed, submittedFiltersCount }) => {
  const areFilters = submittedFiltersCount > 0;
  const isPlural = submittedFiltersCount > 1;
  const label = `Filter${isPlural ? "s" : ""}${submittedFiltersCount > 0 ? ` (${submittedFiltersCount})` : ""}`;
  return (
    <ButtonBase
      onClick={onPressed}
      className={
        areFilters
          ? "filters-bar__chip filters-bar__chip--active"
          : "filters-bar__chip"
      }
      sx={{
        border: "1px solid",
        borderColor: areFilters ? "primary.main" : colors.core50,
        backgroundColor: areFilters ? "primary.light" : "transparent",
        borderRadius: "100px",
        padding: "6px 8px 6px 16px",
        fontSize: "16px",
        fontFamily: "inherit",
        textTransform: "none",
        gap: "8px",
        color: areFilters ? "primary.main" : colors.core50,
        "&:hover": {
          backgroundColor: areFilters ? "primary.light" : "primary.light",
        },
      }}
    >
      {label}
      <ArrowDropDown
        sx={{ color: areFilters ? "primary.main" : colors.core50 }}
      />
    </ButtonBase>
  );
};
